/**
 * Cross-tab leader election for the softphone.
 *
 * Only one browser tab should hold an active twilio.Device at a time —
 * otherwise both tabs ring on inbound calls and the user sees two answer
 * buttons + conflicting state. This module uses BroadcastChannel to elect
 * a single leader using an optimistic-claim policy with a tab-id tiebreaker
 * (first-tab-wins by id ordering):
 *
 *   1. Every tab optimistically claims leadership on startup and announces
 *      itself. Without this synchronous claim, a fresh tab would have to
 *      wait for an async round-trip before it could start the softphone.
 *   2. On receiving another tab's announcement: if the other tab's id
 *      compares lower (lexicographic), it wins and we step down; otherwise
 *      we hold and re-announce so the newcomer steps down. Both tabs apply
 *      the same comparison, so the outcome is deterministic.
 *   3. On `beforeunload`, the leader broadcasts `stepdown` so a surviving
 *      tab can take over. Followers receiving `stepdown` re-announce, and
 *      whichever non-leader tab has the lowest id wins.
 *
 * Browsers without BroadcastChannel fall back to "always leader" — no
 * cross-tab coordination, behaves exactly like before this module existed.
 */

const CHANNEL_NAME = 'cbcrm-softphone-leader'

type Message =
  | { type: 'announce'; from: string }
  | { type: 'stepdown'; from: string }

// Per-tab id. Date.now suffix is for human-readability in debugging;
// the random prefix is what makes ties astronomically unlikely.
const tabId = Math.random().toString(36).slice(2, 10) + Date.now().toString(36)

let channel: BroadcastChannel | null = null
let isLeader = false
let started = false
const listeners = new Set<(isLeader: boolean) => void>()

function notify(): void {
  for (const fn of listeners) {
    try {
      fn(isLeader)
    } catch {
      /* listener bugs must not break election */
    }
  }
}

function send(msg: Message): void {
  try {
    channel?.postMessage(msg)
  } catch {
    /* channel closed mid-send — ignore */
  }
}

function setLeader(next: boolean): void {
  if (next === isLeader) return
  isLeader = next
  notify()
}

/**
 * Start cross-tab leader election. Idempotent — safe to call from React
 * effect or store init.
 */
export function startLeaderElection(): void {
  if (started) return
  started = true

  if (typeof BroadcastChannel === 'undefined') {
    // No cross-tab coordination available — behave as the only tab.
    isLeader = true
    return
  }

  channel = new BroadcastChannel(CHANNEL_NAME)
  channel.onmessage = (e: MessageEvent<Message>) => {
    const msg = e.data
    if (!msg || msg.from === tabId) return
    switch (msg.type) {
      case 'announce': {
        // Tab-id tiebreaker: the lower id wins. Both tabs apply this rule
        // and converge on the same leader without any extra round-trip.
        if (msg.from < tabId) {
          // The other tab outranks us → step down (if we were leader).
          setLeader(false)
        } else if (isLeader) {
          // We outrank the newcomer → re-announce so they step down.
          send({ type: 'announce', from: tabId })
        }
        break
      }
      case 'stepdown': {
        // Old leader unloaded — re-announce. Whichever non-leader tab has
        // the lowest id will end up holding leadership via the tiebreaker.
        send({ type: 'announce', from: tabId })
        setLeader(true)
        break
      }
    }
  }

  // Optimistically claim leadership synchronously so initDevice can proceed
  // on first call. If another tab outranks us, the next inbound `announce`
  // will demote us within a microtask.
  isLeader = true
  send({ type: 'announce', from: tabId })

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (isLeader) {
        send({ type: 'stepdown', from: tabId })
      }
      try {
        channel?.close()
      } catch {
        /* already closed */
      }
    })
  }
}

/**
 * Subscribe to leader changes. Fires only on subsequent changes, not on
 * initial subscription — callers can read the current value via
 * isThisTabLeader() if they need it.
 * Returns an unsubscribe function.
 */
export function onLeaderChange(fn: (isLeader: boolean) => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function isThisTabLeader(): boolean {
  return isLeader
}

export function getTabId(): string {
  return tabId
}

/**
 * Test-only — reset module state between tests. Production code should
 * never call this.
 */
export function __resetForTests(): void {
  try {
    channel?.close()
  } catch {
    /* no-op */
  }
  channel = null
  isLeader = false
  started = false
  listeners.clear()
}
